import { Link } from "react-router-dom";
import {
  Star,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  FileSearch,
  TrendingDown,
  CheckCircle2,
  Users,
} from "lucide-react";
import { Button } from "../ui/button";
import { useReveal } from "../../lib/animations";
import { FlagsBENL } from "../Flags";

export default function Hero() {
  const ref = useReveal();
  return (
    <section
      data-testid="hero-section"
      className="relative oc-hero-gradient overflow-hidden text-white"
    >
      {/* Layered ambient background — modern depth without external image */}
      <div className="absolute inset-0 oc-grid-bg opacity-[0.18]" />
      <div
        className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle at center, #1B4B8A 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-40 -left-32 w-[700px] h-[700px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle at center, #34C759 0%, transparent 70%)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#003B82]/60 via-[#003B82]/40 to-[#003B82]/90" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-28">
        <div ref={ref} className="oc-reveal grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <div>
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-[12px] uppercase tracking-[0.12em] font-semibold text-white/85"
            >
              <Sparkles className="w-3.5 h-3.5" /> Slimme offerte-analyse
              <span className="mx-1 w-px h-3 bg-white/25" />
              <FlagsBENL size="sm" />
              <span className="text-white/85">BE &amp; NL</span>
            </span>

            <h1 className="font-display font-extrabold text-[34px] sm:text-[52px] lg:text-[64px] xl:text-[72px] leading-[1.02] tracking-[-0.02em] mt-6 text-white">
              Is jouw offerte
              <span className="text-oc-fair"> écht correct geprijsd?</span>
            </h1>

            <p className="mt-5 sm:mt-7 text-white/85 text-[16px] sm:text-[19px] lg:text-[20px] leading-[1.55] max-w-xl">
              Wij vergelijken jouw prijs met wat anderen in jouw regio betalen. In 60 seconden weet je
              of je goed zit — of <span className="text-white font-semibold">hoeveel je bespaart</span>.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
              <Link to="/analyse" className="w-full sm:w-auto">
                <Button
                  data-testid="hero-cta-primary"
                  className="group w-full sm:w-auto bg-white text-oc-primary hover:bg-white hover:scale-[1.02] transition-all duration-200 rounded-oc-btn px-7 sm:px-8 h-13 sm:h-14 text-[15px] sm:text-[16px] font-semibold shadow-[0_12px_40px_rgba(0,0,0,0.3)]"
                >
                  Analyseer mijn offerte — €4,99
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>
              <p className="text-white/65 text-[13px] sm:text-[14px]">
                Geen abonnement · Resultaat in 60 sec
              </p>
            </div>

            {/* Trust badges — 2×2 grid (more breathing room, full labels) */}
            <div className="mt-10 sm:mt-12 grid grid-cols-2 gap-2.5 sm:gap-3 max-w-xl">
              <Badge>
                <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 shrink-0" />
                <span className="font-mono text-[12px] sm:text-[13px] text-white">4,9/5</span>
                <span className="text-white/65 text-[11px] sm:text-[12px]">op 1.247 analyses</span>
              </Badge>
              <Badge>
                <ShieldCheck className="w-3.5 h-3.5 text-oc-fair shrink-0" />
                <span className="text-[11px] sm:text-[12px]">GDPR · 30 dagen</span>
              </Badge>
              <Badge>
                <FlagsBENL size="sm" />
                <span className="text-[11px] sm:text-[12px]">België &amp; Nederland</span>
              </Badge>
              <Badge>
                <Users className="w-3.5 h-3.5 text-oc-fair shrink-0" />
                <span className="text-[11px] sm:text-[12px]">Voor particulieren</span>
              </Badge>
            </div>
          </div>

          {/* RIGHT — visual mock-up */}
          <div className="hidden lg:block">
            <ReportMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[13px] font-medium text-white/90">
      {children}
    </span>
  );
}

/* ---------- Right side: modern animated mock-up ---------- */
function ReportMockup() {
  return (
    <div className="relative">
      {/* Floating accent: savings pill */}
      <div className="absolute -top-6 -right-6 z-20 bg-oc-fair text-white rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgba(52,199,89,0.4)] rotate-3 animate-[float_4s_ease-in-out_infinite]">
        <div className="flex items-center gap-2.5">
          <TrendingDown className="w-5 h-5" strokeWidth={2.5} />
          <div>
            <p className="text-[11px] uppercase tracking-[0.1em] opacity-80 font-semibold">Bespaard</p>
            <p className="font-display text-[22px] font-bold leading-none mt-0.5">€ 2.140</p>
          </div>
        </div>
      </div>

      {/* Main report card */}
      <div className="relative bg-white text-oc-primary rounded-[20px] shadow-[0_30px_80px_rgba(0,0,0,0.4)] overflow-hidden border border-white/10">
        <div className="bg-oc-primary/95 px-5 py-3.5 flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          </div>
          <p className="ml-3 text-white/80 text-[12px] font-mono">offerteexpert.be / rapport</p>
        </div>

        <div className="p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">
                Renovatie · Antwerpen
              </p>
              <h3 className="mt-1.5 font-display font-bold text-[20px] text-oc-primary leading-snug">
                Offerte-analyse rapport
              </h3>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-oc-textSecondary">Score</p>
              <p className="font-display font-bold text-[28px] text-oc-excessive leading-none mt-1">7,8</p>
            </div>
          </div>

          {/* Line items mock */}
          <div className="mt-6 space-y-2.5">
            <LineRow label="Materiaal — gipsplaten" value="€ 1.840" status="ok" />
            <LineRow label="Arbeidsuren" value="€ 4.200" status="warn" />
            <LineRow label="Sloopwerken + afvoer" value="€ 1.090" status="bad" />
            <LineRow label="Elektriciteit aansluitingen" value="€ 720" status="ok" />
          </div>

          {/* Verdict */}
          <div className="mt-6 p-4 rounded-xl bg-oc-excessive/10 border border-oc-excessive/20">
            <div className="flex items-center gap-2.5">
              <FileSearch className="w-4 h-4 text-oc-excessive" />
              <p className="text-[12px] sm:text-[13px] font-semibold text-oc-excessive">
                17% boven marktprijs in Antwerpen
              </p>
            </div>
            <p className="mt-1.5 text-[12px] text-oc-textSecondary leading-relaxed">
              Onderhandel op sloopwerken en uurtarief — gemiddeld bespaart een klant €&nbsp;2.140.
            </p>
          </div>
        </div>
      </div>

      {/* Floating accent: 60-sec pill */}
      <div className="absolute -bottom-5 -left-5 z-20 bg-white text-oc-primary rounded-2xl px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.25)] flex items-center gap-2.5 -rotate-2 animate-[float_5s_ease-in-out_infinite_reverse]">
        <CheckCircle2 className="w-5 h-5 text-oc-fair" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">Klaar in</p>
          <p className="font-display text-[16px] font-bold leading-none mt-0.5">60 seconden</p>
        </div>
      </div>
    </div>
  );
}

function LineRow({ label, value, status }) {
  const colors = {
    ok: "bg-oc-fair",
    warn: "bg-yellow-400",
    bad: "bg-oc-excessive",
  };
  return (
    <div className="flex items-center justify-between gap-3 text-[13px]">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <span className={`w-2 h-2 rounded-full ${colors[status]} shrink-0`} />
        <span className="text-oc-textPrimary truncate">{label}</span>
      </div>
      <span className="font-mono font-semibold text-oc-primary shrink-0">{value}</span>
    </div>
  );
}
